import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FiGithub, 
  FiExternalLink, 
  FiCode,
  FiPlay
} from 'react-icons/fi';
import {
  ProjectsContainer,
  ProjectsContent,
  ProjectsHeader,
  ProjectsTitle,
  ProjectsSubtitle,
  ProjectsGrid,
  ProjectCard,
  ProjectImageContainer,
  ProjectImage,
  ProjectOverlay,
  ProjectContent,
  ProjectCategory,
  ProjectTitle,
  ProjectDescription,
  ProjectTech,
  TechTag,
  ProjectFeatures,
  FeatureItem,
  ProjectLinks,
  ProjectLink,
} from './Projects.styles';
import { projects } from '../../utils/constants';
import useLocale from '../../hooks/useLocale';

const Projects = () => {
  const { t } = useLocale();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [hoveredProject, setHoveredProject] = useState(null);
  


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        staggerChildren: 0.12
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <ProjectsContainer id="projects" className="section section--white">
      <ProjectsContent className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <ProjectsHeader>
            <motion.div variants={itemVariants}>
              <ProjectsTitle>{t('projectsUI.title')}</ProjectsTitle>
            </motion.div>
            <motion.div variants={itemVariants}>
              <ProjectsSubtitle>
                {t('projectsUI.subtitle')}
              </ProjectsSubtitle>
            </motion.div>
         
          </ProjectsHeader>

          <ProjectsGrid>
            {(t('projects') && t('projects').length ? t('projects') : projects).map((project, index) => {
              const base = projects.find(p => p.id === project.id) || {};
              
              return (
                <motion.div key={project.id} variants={itemVariants}>
                  <ProjectCard
                    as={motion.div}
                    onMouseEnter={() => setHoveredProject(project.id)}
                    onHoverEnd={() => setHoveredProject(null)}
                  >
                    <ProjectImageContainer>
                      <ProjectImage $hasImage={base.image}>
                        {base.image ? (
                          <img 
                            src={base.image} 
                            alt={project.title}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className="fallback-icon">
                          <FiCode size={60} />
                        </div>
                      </ProjectImage>
                      <ProjectOverlay $isVisible={hoveredProject === project.id} />
                    </ProjectImageContainer>

                    <ProjectContent>
                      <ProjectCategory>{project.category}</ProjectCategory>
                      <ProjectTitle>{project.title}</ProjectTitle>
                      <ProjectDescription>{project.description}</ProjectDescription>

                      <ProjectTech>
                        {(base.technologies || []).map((tech, techIndex) => (
                          <TechTag key={techIndex}>{tech}</TechTag>
                        ))}
                      </ProjectTech>

                      <ProjectFeatures>
                        {(project.features || base.features || []).slice(0, 3).map((feature, featureIndex) => (
                          <FeatureItem key={featureIndex}>
                            <FiPlay size={12} />
                            {feature}
                          </FeatureItem>
                        ))}
                      </ProjectFeatures>

                      <ProjectLinks>
                        {base.githubUrl ? (
                          <ProjectLink
                            href={base.githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            $primary={false}
                          >
                            <FiGithub />
                            {t('projectsUI.codeLabel')}
                          </ProjectLink>
                        ) : (
                          <ProjectLink
                            $disabled={true}
                            title={base.isCoursework ? t('projectsUI.coursework') + ' - Repository not available' : t('projectsUI.private')}
                          >
                            <FiGithub />
                            {base.isCoursework ? t('projectsUI.coursework') : t('projectsUI.private')}
                          </ProjectLink>
                        )}
                        
                        {base.liveUrl ? (
                          <ProjectLink
                            href={base.liveUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            $primary={true}
                            $noHover={true}
                          >
                            <FiExternalLink />
                            {t('projectsUI.liveLabel')}
                          </ProjectLink>
                        ) : base.githubUrl ? (
                          <ProjectLink
                            href={base.githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            $primary={true}
                          >
                            <FiGithub />
                            {t('projectsUI.viewProject')}
                          </ProjectLink>
                        ) : (
                          <ProjectLink
                            $disabled={true}
                            $primary={true}
                            title={t('projectsUI.courseProject')}
                          >
                            <FiCode />
                            {t('projectsUI.courseProject')}
                          </ProjectLink>
                        )}
                      </ProjectLinks>
                    </ProjectContent>
                  </ProjectCard>
                </motion.div>
              );
            })}
          </ProjectsGrid>
        </motion.div>
      </ProjectsContent>
    </ProjectsContainer>
  );
};

export default Projects;